import React, { useState, useEffect } from 'react';
import { useHeaderHeight } from '@react-navigation/elements';
import constants from "../../utils/constants";
import { StyleSheet, ActivityIndicator, Text, View, FlatList, Button, Alert, Pressable, Platform } from 'react-native'
import { AntDesign, MaterialIcons } from '@expo/vector-icons';
import { SearchBar } from 'react-native-elements';
import { db } from '../../db/firestore';
import { doc, getDoc, getDocs, updateDoc, collection, query, where, orderBy } from "firebase/firestore";
import { searchAlgolia } from '../../db/algolia.js';

const LegislationTracking = ({ route, userInfo }) => {
    const [legislation, setLegislation] = useState([]);
    const [legislationTitles, setLegislationTitles] = useState([]);
    const [currentLegislationTitles, setCurrentLegislationTitles] = useState([]);
    const [trackedLanguageBills, setTrackedLanguageBills] = useState([]);
    const [trackedCommitteeNames, setTrackedCommitteeNames] = useState([]);
    const [trackedEvents, setTrackedEvents] = useState([]);
    const [data, setData] = useState([]);
    const [search, setSearch] = useState('');
    const [algoliaNbPages, setAlgoliaNbPages] = useState();
    const [currentAlgoliaPage, setCurrentAlgoliaPage] = useState();
    const [pageLoading, setPageLoading] = useState(true);
    const [searchLoading, setSearchLoading] = useState(false);
    const [billSearch, setBillSearch] = useState(false);
    const headerHeight = useHeaderHeight();

    const chamberType = {
        1: "H",
        2: "S",
    };
    const documentType = {
        1: "B",
        2: "R",
    };

    const buildLegisTitle = (item) => {
        return `${chamberType[item.chamber]}${documentType[item.documentType]}${item.number}`;
    }

    useEffect(() => {
        if (userInfo.trackedLegislationTitles.length !== currentLegislationTitles.length) {
            setLegislation(userInfo.trackedLegislation ? userInfo.trackedLegislation : []);
            setLegislationTitles(userInfo.trackedLegislationTitles ? userInfo.trackedLegislationTitles : []);
            setTrackedLanguageBills(userInfo.trackedLanguageBills ? userInfo.trackedLanguageBills : []);
            setTrackedCommitteeNames(userInfo.trackedCommitteeNames ? userInfo.trackedCommitteeNames : []);
            setTrackedEvents(userInfo.allTrackedEvents ? userInfo.allTrackedEvents : []);
            setCurrentLegislationTitles(userInfo.trackedLegislationTitles ? userInfo.trackedLegislationTitles : [])
            setPageLoading(false);
        }
    }, [userInfo]);

    const handleSave = async (item) => {
        const userRef = doc(db, "users", userInfo.email);
        const eventRef = collection(db, "trackedEvents");
        const billName = buildLegisTitle(item);
        const newLegArr = legislation;
        const newTitleArr = legislationTitles;
        newLegArr.push({
            id: item.id,
            title: item.title,
            legislationTitle: billName,
        });
        newTitleArr.push(billName);
        setLegislation(newLegArr);
        setLegislationTitles(newTitleArr);

        const q = query(eventRef,
            where('billsResolutions', 'array-contains', billName),
        )
        const querySnapshot = await getDocs(q);
        querySnapshot.forEach((doc) => {
            trackedEvents.push(doc.data().id);
        });

        const allTrackedArr = [...new Set(trackedEvents)];

        await updateDoc(userRef, {
            trackedLegislation: newLegArr,
            trackedLegislationTitles: newTitleArr,
            allTrackedEvents: allTrackedArr
        });
    }

    const legisTitleSearch = async (text) => {
        const chamberNumType = {
            H: 1,
            S: 2,
        };
        const documentNumType = {
            B: 1,
            R: 2,
        };
        const formattedText = text.toUpperCase();
        const docRef = collection(db, 'legislation');
        const q = await query(docRef,
            where('chamber', '==', chamberNumType[formattedText[0]]),
            where('documentType', '==', documentNumType[formattedText[1]]),
            where('number', '==', formattedText.substring(2)),
            orderBy("number")
        )
        return await getDocs(q)
            .then((querySnapshot) => {
                const trackedBillArr = [];
                querySnapshot.forEach((doc) => {
                    const item = doc.data()
                    trackedBillArr.push({
                        chamber: item.chamber,
                        documentType: item.documentType,
                        number: item.number,
                        title: item.title,
                        id: item.legislationId,
                        status: item.status,
                    })
                })
                return trackedBillArr.slice()
            })
    }

    const handleAlgoliaSearch = async (text, pageNum) => {
        const billArr = [];
        const { hits, nbPages } = await searchAlgolia(text, pageNum)
        for (let i = 0; i < hits.length; i += 1) {
            billArr.push({
                chamber: hits[i].chamber,
                documentType: hits[i].documentType,
                number: hits[i].number,
                title: hits[i].title,
                id: hits[i].legislationId,
                status: hits[i].status,
            })
        }
        return { billArr, nbPages }
    }

    const handleSearch = async (text) => {
        setSearch(text)
        const dataArr = [];
        if (text.length > 2) {
            setSearchLoading(true)
            const regex = /^(HB|HR|SB|SR)\d+$/i;
            if (regex.test(text)) {
                setBillSearch(true)
                const billArr = await legisTitleSearch(text)
                dataArr.push(...billArr)
            } else {
                setBillSearch(false)
                const { billArr, nbPages } = await handleAlgoliaSearch(text, 0)
                setAlgoliaNbPages(nbPages)
                setCurrentAlgoliaPage(0)
                dataArr.push(...billArr)
            }
            setData(dataArr)
            setSearchLoading(false)
        } else {
            setData([])
            setSearchLoading(false)
        }

    }

    const moreSearchResults = async (pageNum) => {
        if (!billSearch) {
            if (currentAlgoliaPage > algoliaNbPages) {
                return
            } else {
                const { billArr } = await handleAlgoliaSearch(search, pageNum)
                setCurrentAlgoliaPage(pageNum)
                setData([...data, ...billArr])
            }
        }
    }

    const handleDeleteBill = async (item) => {
        const billName = item.legislationTitle ? item.legislationTitle : buildLegisTitle(item);
    
        // Optimistically update UI
        const newTracked = legislation.filter(e => e.legislationTitle !== billName);
        const newTrackedTitles = legislationTitles.filter(e => e !== billName);
        setLegislation(newTracked);
        setLegislationTitles(newTrackedTitles);
    
        try {
            const userRef = doc(db, "users", userInfo.email);
            const eventRef = collection(db, "trackedEvents");
    
            // Query events associated with this bill
            const q = query(eventRef, where('billsResolutions', 'array-contains', billName));
            const querySnapshot = await getDocs(q);
    
            // Prepare updated trackedEvents
            let updatedTrackedEvents = [...new Set(trackedEvents)];
    
            querySnapshot.forEach((doc) => {
                const eventData = doc.data();
                const { committees, billsResolutions } = eventData;
    
                // Check if event is associated with any tracked committees
                const hasTrackedCommittees = committees?.some(c => trackedCommitteeNames.includes(c));
    
                // Check if event is associated with any other tracked bills/resolutions
                const hasOtherTrackedBills = billsResolutions?.some(bill =>
                    newTrackedTitles.includes(bill) ||
                    trackedLanguageBills.some(obj => obj.bill === bill)
                );
    
                // If no other associations exist, remove the event from trackedEvents
                if (!hasTrackedCommittees && !hasOtherTrackedBills) {
                    updatedTrackedEvents = updatedTrackedEvents.filter(eventId => eventId !== eventData.id);
                }
            });
    
            // Update Firestore
            await updateDoc(userRef, {
                trackedLegislation: newTracked,
                trackedLegislationTitles: newTrackedTitles,
                allTrackedEvents: updatedTrackedEvents,
            });
    
            // Update state
            setTrackedEvents(updatedTrackedEvents);
        } catch (error) {
            // Revert state if update fails
            setLegislation([...newTracked, { id: item.id, title: item.title, legislationTitle: billName }]);
            setLegislationTitles([...newTrackedTitles, billName]);
        }
    };    

    return (
        <View style={{ height: '100%', backgroundColor: constants.PRIMARY_DARK }}>
            <View style={[styles.container, { marginTop: headerHeight }]}>
                <SearchBar
                    placeholder="Search legislation to track"
                    onChangeText={(text) => handleSearch(text)}
                    value={search}
                    clearIcon
                    darkTheme
                    containerStyle={{ backgroundColor: constants.PRIMARY_DARK, width: '98%', alignSelf: 'center', padding: 0, marginBottom: 15 }}
                    inputContainerStyle={{ borderRadius: 9 }}
                />
                {searchLoading ?
                    <ActivityIndicator size="large" color={constants.ICON_COLOR} style={search.length > 0 ? styles.shown : styles.hidden} />
                    :
                (data.length > 0)
                    ? <FlatList
                        style={styles.scrollView}
                        data={data}
                        renderItem={({ item }) => (
                            <View
                                style={[styles.button, styles.buttonClose]}
                            >
                                <View style={styles.rowContainer}>
                                    <View style={{ flex: 5, justifyContent: 'center' }}>
                                        <Text style={styles.textStyle}>{buildLegisTitle(item)}: {item.title}</Text>
                                    </View>
                                    <View style={{ flex: .5, justifyContent: 'center' }}>
                                        {(legislation.some(bill => bill.id === item.id)) ?
                                            <Pressable
                                                onPress={() => handleDeleteBill(item)}
                                            >
                                                <MaterialIcons name="cancel" size={24} color="gray" />
                                            </Pressable>
                                            :
                                            <Pressable
                                                onPress={() => handleSave(item)}
                                            >
                                                <AntDesign name="pluscircle" size={24} color={constants.ICON_COLOR} />
                                            </Pressable>
                                        }
                                    </View>
                                </View>
                            </View>
                        )}
                        keyExtractor={item => item.id}
                        onEndReached={() => moreSearchResults(currentAlgoliaPage + 1)}
                        onEndReachedThreshold={0.01}
                        scrollEventThrottle={150}
                    />
                    : null
                }
                {pageLoading ?
                    <ActivityIndicator size="large" color={constants.ICON_COLOR} />
                    :
                    (legislation.length > 0 && data.length === 0) ?
                        <View style={{flex: 1}}>
                            <View style={{ margin: 15 }}>
                                <Text style={styles.textStyle}>Tracked Legislation</Text>
                            </View>
                            <FlatList
                                style={styles.scrollView}
                                data={legislation}
                                renderItem={({ item }) => (
                                    <View style={[styles.button, styles.buttonClose]}>
                                        <View style={styles.rowContainer}>
                                            <View style={{ flex: 5, justifyContent: 'center' }}>
                                                <Text style={[styles.textStyle, { flex: 1, flexWrap: 'wrap' }]}>{item.legislationTitle}: {item.title}</Text>
                                            </View>
                                            <View style={{ flex: .5, justifyContent: 'center' }}>
                                                <Pressable
                                                    onPress={() => handleDeleteBill(item)}
                                                >
                                                    <MaterialIcons name="cancel" size={24} color={constants.ICON_COLOR} />
                                                </Pressable>
                                            </View>
                                        </View>
                                    </View>
                                )}
                            />
                        </View>
                        : null
                }
            </View>
        </View>
    )
}

export default LegislationTracking

const styles = StyleSheet.create({
    container: {
        backgroundColor: constants.PRIMARY_DARK,
        height: '100%',
        flex: 1
    },
    rowContainer: {
        marginTop: 10,
        marginBottom: 10,
        flexDirection: 'row',
    },
    scrollView: {
        marginBottom: 20,
    },
    button: {
        borderRadius: 5,
        borderWidth: 2,
        borderColor: '#131313',
        paddingTop: 10,
        paddingLeft: 15,
        paddingRight: 15,
        paddingBottom: 12,
        elevation: 2,
        alignSelf: 'center',
        width: "98%",
        marginBottom: 1,
    },
    buttonOpen: {
        backgroundColor: "white",
    },
    buttonClose: {
        backgroundColor: constants.SECONDARY_DARK,
    },
    textStyle: {
        color: constants.TITLE_COLOR,
        width: "90%",
        marginLeft: 7,
        marginBottom: 2,
        fontSize: 14,
        fontWeight: "bold",
        fontFamily: 'Verdana',
    },
    subTextStyle: {
        color: constants.SUBTEXT_COLOR,
        width: "95%",
        height: 20,
        marginLeft: 7,
        fontSize: 12,
        fontWeight: "bold",
        textAlignVertical: "center",
        ...Platform.select({
            ios: {
                lineHeight: 20
            },
            android: {}
        })
    },
})